import { memo, useCallback } from "react";
import { View } from "react-native";
import { SharedValue } from "react-native-reanimated";
import {
	GatheringHeaderInfoFragmentFragment,
	GetGatheringForPublicPreviewQuery,
} from "src/api/graphql/__generated__/graphql";
import GatheringHeader from "src/components/GatheringHeader";
import { SharedGatheringHeaderProps } from "src/components/GatheringHeader/types";
import { HEADER_SPACING } from "src/components/Header/constants";
import RoundedButton from "src/components/RoundedButton";
import Touchable from "src/components/Touchable";
import { parseGatheringThumbnailUrl } from "src/constants/GatheringThumbnail";
import parseGatheringPreviewTemplateUrl from "src/helpers/parseV0TemplateUrl/parseGatheringPreviewTemplateUrl";
import useDevice from "src/hooks/useDevice";
import useRouter from "src/hooks/useRouter";
import useGatheringHomePhotoSpacing from "src/screens/(internal)/(GatheringHome)/GatheringHome/hooks/useGatheringHomePhotoSpacing";
import { paddingLarge } from "src/styles/spacing";
import { Image, Text, useSafeArea } from "src/swsh-native";
import GatheringPublicReport from "../../../GatheringPublicReport";
import { useGatheringPreviewPublicStore } from "../../hooks/useGatheringPreviewPublicStore";
import useGatheringPublicPreviewInfo from "../../hooks/useGatheringPublicPreviewInfo";
import GatheringPreviewPublicTags from "./components/GatheringPreviewPublicTags";

const GatheringPreviewPublicHeader = ({
	scrollOffset,
	thumbnail,
}: {
	scrollOffset: SharedValue<number>;
	thumbnail: GatheringHeaderInfoFragmentFragment["thumbnail"];
}) => {
	const gatheringId = useGatheringPreviewPublicStore((state) => state.gatheringId);
	const gatheringName = useGatheringPreviewPublicStore((state) => state.gatheringName);
	const joinLoading = useGatheringPreviewPublicStore((state) => state.joinLoading);
	const handleJoinGathering = useGatheringPreviewPublicStore(
		(state) => state.handleJoinGathering,
	);
	const { gathering } = useGatheringPublicPreviewInfo(gatheringId);

	const renderWheelItem = useCallback<
		NonNullable<
			SharedGatheringHeaderProps<
				NonNullable<
					NonNullable<GetGatheringForPublicPreviewQuery["getGathering"]>["previewPhotos"]
				>["payload"][number]
			>["renderWheelItem"]
		>
	>(({ item, width, height, opacity, borderRadius }) => {
		const src = parseGatheringPreviewTemplateUrl({
			readUrl: item?.readUrl,
		});
		if (!src) return null;
		return (
			<Image
				alt="Photo"
				src={src}
				dim={{
					width,
					height,
				}}
				style={{
					borderRadius,
					overflow: "hidden",
					opacity,
				}}
			/>
		);
	}, []);
	const { device, native } = useDevice();
	const safeArea = useSafeArea();
	const router = useRouter();
	const { containerHorizontalPadding } = useGatheringHomePhotoSpacing();

	return (
		<View
			style={{
				alignItems: "center",
			}}
		>
			<GatheringHeader
				testID="GatheringPreviewHeader"
				wheelData={gathering?.previewPhotos?.payload}
				renderWheelItem={renderWheelItem}
				name={gathering?.name ?? gatheringName}
				variant={device === "desktop" ? "Wide" : "Regular"}
				wideOptions={{ horizontalPadding: containerHorizontalPadding }}
				regularOptions={{ scrollOffset }}
				thumbnailUrl_1x1={parseGatheringThumbnailUrl({
					readUrl: gathering?.thumbnail?.readUrl ?? thumbnail?.readUrl,
				})}
				renderEndSection={() => (
					<RoundedButton
						loading={joinLoading}
						onPress={handleJoinGathering}
						sizeVariant="Large"
						colorVariant="Primary"
					>
						Join album
					</RoundedButton>
				)}
				renderRowActions={() => [<GatheringPreviewPublicTags key="viewers" />]}
			/>
			<View
				style={{
					position: "absolute",
					paddingTop: HEADER_SPACING.paddingTop + safeArea.top,
					flexDirection: "row",
					justifyContent: "space-between",
					width: "100%",
					paddingHorizontal: paddingLarge + containerHorizontalPadding,
				}}
			>
				{native ? (
					<Touchable onPress={router.back}>
						<Text semibold>Exit</Text>
					</Touchable>
				) : (
					<View />
				)}
				<GatheringPublicReport gatheringId={gatheringId} />
			</View>
		</View>
	);
};
export default memo(GatheringPreviewPublicHeader);
